import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip } from '@mui/material'
import { Add } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { listInvoices, type InvoiceListItem, type InvoiceKind, type InvoiceStatus } from '../../api/financiar'
import GenerateInvoiceDialog from './GenerateInvoiceDialog'
import InvoiceDetailDialog from './InvoiceDetailDialog'

const KIND_LABEL: Record<InvoiceKind, string> = { Proforma: 'Proformă', Fiscala: 'Fiscală', NotaCredit: 'Notă de credit' }
const STATUS_LABEL: Record<InvoiceStatus, string> = { Draft: 'Ciornă', Emisa: 'Emisă', Stornata: 'Stornată' }
const STATUS_COLOR: Record<InvoiceStatus, 'default' | 'success' | 'error'> = { Draft: 'default', Emisa: 'success', Stornata: 'error' }

export default function FacturiPage() {
  const [generateOpen, setGenerateOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'facturi'], queryFn: () => listInvoices() })

  const columns = useMemo<GridColDef<InvoiceListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 150, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'kind', headerName: 'Tip', width: 110, renderCell: (p) => KIND_LABEL[p.row.kind] },
    {
      field: 'status', headerName: 'Status', width: 130, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    { field: 'clientName', headerName: 'Client', flex: 1, minWidth: 180 },
    { field: 'issueDate', headerName: 'Emitere', width: 110 },
    { field: 'dueDate', headerName: 'Scadență', width: 110 },
    { field: 'total', headerName: 'Total', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    {
      field: 'overdueDays', headerName: 'Restanță', width: 110, sortable: false,
      renderCell: (p) => p.row.overdueDays ? <Chip size="small" color="error" label={`${p.row.overdueDays} zile`} /> : p.row.paidAt ? <Chip size="small" color="success" variant="outlined" label="Plătită" /> : null,
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Financiar</Typography>
        <Typography variant="inherit" color="text.primary">Facturi emise</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Facturi emise</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setGenerateOpen(true)}>Generează factură</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={(p) => setDetailId(p.row.id)}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <GenerateInvoiceDialog open={generateOpen} onClose={() => setGenerateOpen(false)} onCreated={(id) => { setGenerateOpen(false); setDetailId(id) }} />
      <InvoiceDetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </Stack>
  )
}
